/**
 * The PDF container format, at the smallest size that is still a real PDF.
 *
 * Written by hand rather than pulled from a library, for one reason that matters and one that is
 * pleasant. The one that matters: this has to run unchanged in a browser, in a Web Worker and in
 * a Cloudflare Worker, and it has to keep working when a map is 10,000 nodes — which rules out
 * anything that assumes a DOM and makes a dependency's own constraints into ours. The pleasant
 * one: what a PDF needs here is a catalogue, some pages, content streams and a font, and none of
 * that is hard when the fonts are the standard fourteen.
 *
 * **No font embedding.** Every conforming reader has Helvetica, so the text stays text without a
 * single byte of font data. The cost is stated where it bites, in `winAnsi`: the standard
 * encoding covers Latin-1 exactly and nothing beyond it. A product shipping in twelve languages
 * will need an embedded face for the rest, and that is a known increment rather than a surprise.
 *
 * The whole file is ASCII by construction — bytes above 126 are written as octal escapes inside
 * strings — so a character offset is a byte offset, which is what makes the cross-reference table
 * correct without a second pass over encoded bytes.
 */
export const A4_PORTRAIT = { width: 595.28, height: 841.89 };
export const LETTER_PORTRAIT = { width: 612, height: 792 };
export function landscape(size) {
    return { width: size.height, height: size.width };
}
/**
 * Characters WinAnsi puts in the 128–159 range, where it differs from Latin-1.
 *
 * Small and worth having: these are the curly quotes and dashes a word processor produces, so a
 * title pasted from one arrives intact instead of as a row of question marks.
 */
const WIN_ANSI_SPECIALS = new Map([
    [0x20ac, 128], // €
    [0x201a, 130],
    [0x0192, 131],
    [0x201e, 132],
    [0x2026, 133], // …
    [0x2020, 134],
    [0x2021, 135],
    [0x02c6, 136],
    [0x2030, 137],
    [0x0160, 138],
    [0x2039, 139],
    [0x0152, 140],
    [0x017d, 142],
    [0x2018, 145], // ‘
    [0x2019, 146], // ’
    [0x201c, 147], // “
    [0x201d, 148], // ”
    [0x2022, 149], // •
    [0x2013, 150], // –
    [0x2014, 151], // —
    [0x02dc, 152],
    [0x2122, 153], // ™
    [0x0161, 154],
    [0x203a, 155],
    [0x0153, 156],
    [0x017e, 158],
    [0x0178, 159],
]);
/**
 * A string as a PDF literal in WinAnsi.
 *
 * The substitution is deliberate and counted rather than silent. A map in Japanese exported to
 * PDF today comes out as question marks, and the export has to be able to say so — a file that
 * quietly replaced someone's text with punctuation would be worse than one that refused.
 */
export function winAnsi(text) {
    let literal = '';
    let unsupported = 0;
    for (const character of text) {
        const code = character.codePointAt(0) ?? 0;
        if (character === '(' || character === ')' || character === '\\') {
            literal += `\\${character}`;
            continue;
        }
        if (code >= 32 && code <= 126) {
            literal += character;
            continue;
        }
        const special = WIN_ANSI_SPECIALS.get(code);
        const byte = special ?? (code >= 160 && code <= 255 ? code : null);
        if (byte === null) {
            literal += '?';
            unsupported += 1;
            continue;
        }
        // Octal, so the file stays ASCII and a character offset stays a byte offset.
        literal += `\\${byte.toString(8).padStart(3, '0')}`;
    }
    return { literal, unsupported };
}
/** Rounds to a sensible number of decimals and never emits `1e-7`, which a PDF reader rejects. */
export function num(value) {
    const rounded = Math.round(value * 1000) / 1000;
    return Object.is(rounded, -0) ? '0' : String(rounded);
}
/**
 * Assembles pages into a file.
 *
 * Object numbering is fixed rather than discovered: 1 is the catalogue, 2 the page tree, 3 the
 * font, 4 the metadata, and pages and their contents follow in pairs. A PDF's cross-reference table is byte
 * offsets, so anything that shifted an object after its offset was recorded would produce a file
 * that opens in some readers and not others — the worst kind of wrong.
 */
export function buildPdf(pages, metadata = {}) {
    const CATALOG = 1;
    const PAGE_TREE = 2;
    const FONT = 3;
    const INFO = 4;
    const FIRST_PAGE = 5;
    const pageNumber = (index) => FIRST_PAGE + index * 2;
    const contentNumber = (index) => FIRST_PAGE + index * 2 + 1;
    const objects = new Map();
    objects.set(CATALOG, `<< /Type /Catalog /Pages ${String(PAGE_TREE)} 0 R >>`);
    const kids = pages.map((_, index) => `${String(pageNumber(index))} 0 R`).join(' ');
    objects.set(PAGE_TREE, `<< /Type /Pages /Kids [${kids}] /Count ${String(pages.length)} >>`);
    objects.set(FONT, '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>');
    // Always written, even with no title: an object number that sometimes exists would shift every
    // page number after it, and page numbers are what the cross-reference table is built from.
    const named = metadata.title === undefined ? '' : ` /Title (${winAnsi(metadata.title).literal})`;
    objects.set(INFO, `<< /Producer (Orbit Mind)${named} >>`);
    pages.forEach((page, index) => {
        objects.set(pageNumber(index), `<< /Type /Page /Parent ${String(PAGE_TREE)} 0 R ` +
            `/MediaBox [0 0 ${num(page.size.width)} ${num(page.size.height)}] ` +
            `/Resources << /Font << /F1 ${String(FONT)} 0 R >> >> ` +
            `/Contents ${String(contentNumber(index))} 0 R >>`);
        objects.set(contentNumber(index), `<< /Length ${String(page.content.length)} >>\nstream\n${page.content}\nendstream`);
    });
    const total = objects.size;
    const offsets = new Map();
    let file = '%PDF-1.7\n';
    for (let number = 1; number <= total; number += 1) {
        const body = objects.get(number);
        if (body === undefined)
            continue;
        offsets.set(number, file.length);
        file += `${String(number)} 0 obj\n${body}\nendobj\n`;
    }
    const xrefAt = file.length;
    file += `xref\n0 ${String(total + 1)}\n`;
    // Object zero is always the head of the free list, and always exactly this.
    file += '0000000000 65535 f \n';
    for (let number = 1; number <= total; number += 1) {
        file += `${String(offsets.get(number) ?? 0).padStart(10, '0')} 00000 n \n`;
    }
    file +=
        `trailer\n<< /Size ${String(total + 1)} /Root ${String(CATALOG)} 0 R ` +
            `/Info ${String(INFO)} 0 R >>\n`;
    file += `startxref\n${String(xrefAt)}\n%%EOF\n`;
    // Latin-1 rather than UTF-8: the string is ASCII by construction, and this makes that an
    // assertion rather than an assumption — a stray multi-byte character would corrupt the offsets.
    const bytes = new Uint8Array(file.length);
    for (let index = 0; index < file.length; index += 1) {
        bytes[index] = file.charCodeAt(index) & 0xff;
    }
    return bytes;
}
export const PDF_MIME_TYPE = 'application/pdf';
//# sourceMappingURL=document.js.map